import { useState, type ChangeEvent, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Input } from "@agentnative/ui";
import { AuthLayout, authInputClass, authButtonClass, authLinkClass, authLinkRowClass } from "../components/AuthLayout";
import { login, fetchCsrfToken } from "../lib/api/auth";
import { apiErrorMessage } from "../lib/api/client";
import { useAuthStore } from "../stores/auth";

// safeReturnTo extracts the ?return_to= the console server attaches when it
// bounces an unauthenticated /oauth/authorize request here (see
// console-server's oauth_handlers.go). Only a same-origin /oauth/authorize
// path is honored -- anything else (absolute URLs, protocol-relative
// "//host", other paths) is ignored, so this can't be used as an open
// redirect.
function safeReturnTo(): string | null {
  const raw = new URLSearchParams(window.location.search).get("return_to");
  if (!raw || !raw.startsWith("/oauth/authorize?")) {
    return null;
  }
  return raw;
}

export function LoginRoute() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      // Bootstrap a CSRF cookie first: the login POST itself is a
      // mutating, CSRF-checked request once an access-token cookie is
      // present, but on a first visit there is no access-token cookie
      // yet -- console-server's csrfMiddleware only enforces the
      // double-submit check when an access-token cookie exists (see
      // console/cmd/console-server/csrf.go's requiresCSRFCheck), so this
      // call isn't strictly required for login itself, but running it
      // unconditionally keeps the CSRF cookie fresh for the requests
      // that follow immediately after (and mirrors the flow a
      // real login page should use before any mutating call).
      await fetchCsrfToken();
      return login({ email, password });
    },
    onSuccess: (resp) => {
      if (!resp.success || !resp.user) {
        return;
      }
      setUser(resp.user);
      const returnTo = safeReturnTo();
      if (returnTo) {
        // Full-page navigation, not router navigate: /oauth/authorize is a
        // server endpoint that issues a redirect back to the relying
        // party, not an SPA route.
        window.location.assign(returnTo);
        return;
      }
      navigate({ to: "/" });
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    mutation.mutate();
  }

  const loginFailed = mutation.isSuccess && !mutation.data.success;

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to your Agent Native account">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {mutation.isError && (
          <Alert variant="danger" title="Login failed">
            {apiErrorMessage(mutation.error)}
          </Alert>
        )}
        {loginFailed && (
          <Alert variant="danger" title="Login failed">
            {mutation.data.message}
          </Alert>
        )}
        <Input
          className={authInputClass}
          label="Email"
          id="login-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
        />
        <Input
          className={authInputClass}
          label="Password"
          id="login-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
        />
        <Button type="submit" variant="primary" className={authButtonClass} disabled={mutation.isPending}>
          {mutation.isPending ? "Signing in..." : "Sign in"}
        </Button>
        <div className={authLinkRowClass}>
          <a href="/register" className={authLinkClass}>
            Create an account
          </a>
          <a href="/forgot-password" className={authLinkClass}>
            Forgot password?
          </a>
        </div>
      </form>
    </AuthLayout>
  );
}
